import { type Middleware } from 'redux'
import { createAction } from '@reduxjs/toolkit'
import { LockSuccessAction, LogoutSuccessAction, UserReceivedAction } from './auth/auth-actions'

/* Types */

interface PersistedData {
  idToken: string | null
  tab: number
  isAdmin: boolean
  isAssignedToCompany: boolean
}

/* Action Types */
const PERSISTENCE_LOAD = 'PERSISTENCE_LOAD'
const PERSISTENCE_RESTORE = 'PERSISTENCE_RESTORE'

/* Action Creators */

export const loadPersistedDataAction = createAction(PERSISTENCE_LOAD)
export const RestorePersistedDataAction = createAction<PersistedData>(PERSISTENCE_RESTORE)

/* Exported Functions */

export const persistenceMiddleware: Middleware = api => next => action => {
  if (loadPersistedDataAction.match(action)) {
    api.dispatch(RestorePersistedDataAction(loadAllData()))
  }

  if (UserReceivedAction.match(action)) {
    persistData('username', action.payload.user.username)
    persistData('admin', String(action.payload.user.systemAdmin))
    persistData('assigned', String(action.payload.assignedToCompany))
    persistData('ownCompany', JSON.stringify(action.payload.ownCompany))
  }

  if (LockSuccessAction.match(action)) {
    persistData('id_token', action.payload.token)
  }

  if (LogoutSuccessAction.match(action)) {
    removeData(['admin', 'id_token', 'username', 'assigned', 'ownCompany', 'meterData'])
  }

  return next(action)
}

/* Helper Functions */

function loadAllData(): PersistedData {
  return {
    idToken: loadData('id_token'),
    tab: toNumber(loadData('tab'), 0),
    isAdmin: isTrue(loadData('admin')),
    isAssignedToCompany: isTrue(loadData('assigned'))
  }
}

function persistData(key: string, value: string): void {
  localStorage.setItem(key, value)
}

function removeData(keys: string[]): void {
  for (const key of keys) {
    localStorage.removeItem(key)
  }
}

function loadData(key: string): string | null {
  return localStorage.getItem(key)
}

function isTrue(localStorageValue: string | null): boolean {
  return (localStorageValue ?? '').toLowerCase() === 'true'
}

function toNumber(localStorageValue: string | null, defaultValue: number): number {
  if (localStorageValue == null) {
    return defaultValue
  }

  return parseInt(localStorageValue, 10) ?? defaultValue
}
